// Paginador genérico, pensado primero para móvil.
// Móvil:      ‹ 1 … 5 … 207 ›            (una sola línea)
// Escritorio: « Anterior 1 … 4 5 6 … 207 Siguiente »
import React from 'react';

// Páginas a mostrar alrededor de la actual (siblings a cada lado); null = hueco ("…")
const getPageItems = (page, totalPages, siblings) => {
  const pages = new Set([1, totalPages]);
  for (let p = page - siblings; p <= page + siblings; p++) pages.add(p);
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  const items = [];
  sorted.forEach((p, i) => {
    // Si el hueco es de una sola página, se muestra su número en vez de "…"
    if (i > 0 && p - sorted[i - 1] === 2) items.push(p - 1);
    else if (i > 0 && p - sorted[i - 1] > 2) items.push(null);
    items.push(p);
  });
  return items;
};

const Pagination = ({ page, pageSize, total, totalPages, onPageChange, disabled = false }) => {
  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // h-11 = 44px: tamaño mínimo cómodo para el dedo
  const baseBtn = 'inline-flex items-center justify-center h-11 min-w-[2.5rem] px-1.5 sm:min-w-[2.75rem] sm:px-3 text-base font-bold rounded-lg border select-none focus:outline-none focus:ring-2 focus:ring-blue-500';
  const idleBtn = `${baseBtn} bg-white text-gray-800 border-gray-300 hover:bg-blue-50 hover:border-blue-400 active:bg-blue-100`;
  const activeBtn = `${baseBtn} bg-blue-600 text-white border-blue-600 shadow`;
  const disabledBtn = `${baseBtn} bg-white text-gray-300 border-gray-200 cursor-not-allowed`;

  const isFirst = page <= 1;
  const isLast = page >= totalPages;

  const renderPages = (siblings) =>
    getPageItems(page, totalPages, siblings).map((p, i) =>
      p === null ? (
        <span key={`gap-${i}`} className="text-gray-500 sm:px-0.5" aria-hidden="true">…</span>
      ) : (
        <button
          key={p}
          type="button"
          className={p === page ? activeBtn : idleBtn}
          aria-current={p === page ? 'page' : undefined}
          aria-label={`Página ${p}`}
          disabled={disabled}
          onClick={() => p !== page && onPageChange(p)}
        >
          {p}
        </button>
      )
    );

  return (
    <div className="flex flex-col items-center gap-3 pt-5 mt-2 font-bold border-t border-gray-200">
      <div className="text-base text-gray-700">
        Mostrando {from}–{to} de {total}
      </div>

      <nav className="flex flex-nowrap items-center justify-center gap-1 sm:gap-2" aria-label="Paginación">
        <button
          type="button"
          className={isFirst || disabled ? disabledBtn : idleBtn}
          disabled={isFirst || disabled}
          aria-label="Página anterior"
          onClick={() => onPageChange(page - 1)}
        >
          <span className="text-xl leading-none sm:hidden">‹</span>
          <span className="hidden sm:inline">« Anterior</span>
        </button>

        <div className="flex items-center gap-1 sm:hidden">{renderPages(0)}</div>
        <div className="items-center hidden gap-2 sm:flex">{renderPages(1)}</div>

        <button
          type="button"
          className={isLast || disabled ? disabledBtn : idleBtn}
          disabled={isLast || disabled}
          aria-label="Página siguiente"
          onClick={() => onPageChange(page + 1)}
        >
          <span className="text-xl leading-none sm:hidden">›</span>
          <span className="hidden sm:inline">Siguiente »</span>
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
